import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { apiClient, buildQueryString, withMinDelayThunk, withMinDelay } from '../../utils/api/';
import { TODO_STATUS_COLUMNS } from '../../constants/todos';

const DEFAULT_TODO_FILTERS = {
  priority: 'ALL',
  search: '',
};

// Async Thunks

export const fetchTodos = createAsyncThunk(
  'todos/fetchTodos',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { initialFetchDone } = getState().todos;
      const qs = buildQueryString(params);
      const url = qs ? `/todos?${qs}` : '/todos';
      const response = !initialFetchDone
        ? await withMinDelay(apiClient.get(url))
        : await apiClient.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch todos');
    }
  }
);

export const createTodo = createAsyncThunk(
  'todos/createTodo',
  withMinDelayThunk(async (todoData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/todos', todoData);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create todo');
    }
  })
);

export const updateTodo = createAsyncThunk(
  'todos/updateTodo',
  withMinDelayThunk(async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/todos/${id}`, data);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update todo');
    }
  })
);

export const deleteTodo = createAsyncThunk(
  'todos/deleteTodo',
  withMinDelayThunk(async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/todos/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete todo');
    }
  })
);

export const persistReorder = createAsyncThunk(
  'todos/persistReorder',
  async ({ todoId, beforeId }, { rejectWithValue }) => {
    try {
      await apiClient.patch(`/todos/${todoId}/reorder`, { beforeId });
      return { todoId, beforeId };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to reorder todo');
    }
  }
);

export const updateTodoTags = createAsyncThunk(
  'todos/updateTodoTags',
  withMinDelayThunk(async ({ id, tagNames }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/todos/${id}/tags`, { tagNames });
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update todo tags');
    }
  })
);

const initialState = {
  items: [],
  pageInfo: null,
  filters: DEFAULT_TODO_FILTERS,
  priorityFilterValue: DEFAULT_TODO_FILTERS.priority,
  searchFilterValue: DEFAULT_TODO_FILTERS.search,
  loading: false,
  error: null,
  initialFetchDone: false,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
};

const todosSlice = createSlice({
  name: 'todos',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      const nextFilters = {
        ...DEFAULT_TODO_FILTERS,
        ...(state.filters || {}),
        ...action.payload,
      };

      state.filters = nextFilters;
      state.priorityFilterValue = nextFilters.priority;
      state.searchFilterValue = nextFilters.search;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetStatus: (state) => {
      state.status = 'idle';
    },
    reorderTodo: (state, action) => {
      const { id, beforeId } = action.payload;
      const fromIndex = state.items.findIndex(
        (item) => item._id === id
      );
      if (fromIndex === -1) return;
      const [item] = state.items.splice(fromIndex, 1);
      if (!beforeId) {
        state.items.push(item);
      } else {
        const toIndex = state.items.findIndex(
          (i) => i._id === beforeId
        );
        state.items.splice(toIndex === -1 ? state.items.length : toIndex, 0, item);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Todos
      .addCase(fetchTodos.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchTodos.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || action.payload;
        state.pageInfo = action.payload.pageInfo || null;
        state.initialFetchDone = true;
        state.status = 'succeeded';
      })
      .addCase(fetchTodos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Create Todo
      .addCase(createTodo.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(createTodo.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.status = 'succeeded';
      })
      .addCase(createTodo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Update Todo
      .addCase(updateTodo.pending, (state, action) => {
        state.error = null;
        const { id, data } = action.meta.arg;
        const index = state.items.findIndex(
          (item) => item._id === id
        );
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...data };
        }
      })
      .addCase(updateTodo.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.items.findIndex(
          (item) => item._id === updated._id
        );
        if (index !== -1) {
          state.items[index] = updated;
        }
        state.status = 'succeeded';
      })
      .addCase(updateTodo.rejected, (state, action) => {
        state.error = action.payload;
        state.status = 'failed';
      })
      .addCase(updateTodoTags.pending, (state) => {
        state.error = null;
      })
      .addCase(updateTodoTags.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.items.findIndex(
          (item) => item._id === updated._id
        );
        if (index !== -1) {
          state.items[index] = updated;
        }
        state.status = 'succeeded';
      })
      .addCase(updateTodoTags.rejected, (state, action) => {
        state.error = action.payload;
        state.status = 'failed';
      })
      // Delete Todo
      .addCase(deleteTodo.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(deleteTodo.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item._id !== action.payload);
        state.status = 'succeeded';
      })
      .addCase(deleteTodo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      });
  },
});

export const {
  clearError,
  resetStatus,
  setFilters,
  reorderTodo,
} = todosSlice.actions;

export const selectAllTodos = (state) => state.todos.items;
export const selectTodosLoading = (state) => state.todos.loading;
export const selectTodosInitialFetchDone = (state) => state.todos.initialFetchDone;
export const selectTodosStatus = (state) => state.todos.status;
const selectTodosState = (state) => state.todos;

export const selectTodosFilters = createSelector(
  [selectTodosState],
  (todosState) => {
    if (todosState.filters) {
      return todosState.filters;
    }

    return {
      priority: todosState.priorityFilterValue ?? DEFAULT_TODO_FILTERS.priority,
      search: todosState.searchFilterValue ?? DEFAULT_TODO_FILTERS.search,
    };
  }
);
export const selectTodosPageInfo = (state) => state.todos.pageInfo;

export const selectTodosByStatus = createSelector(
  [selectAllTodos],
  (todos) => {
    const grouped = {};
    for (const col of TODO_STATUS_COLUMNS) {
      grouped[col.id] = todos.filter(todo => todo.status === col.id);
    }
    return grouped;
  }
);

export default todosSlice.reducer;
